import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

// Light/dark theme with a manual toggle. The chosen theme is stored in
// localStorage and applied as a `.dark` class on <html>, which drives both the
// CSS variables in index.css and Tailwind's class-based `dark:` variants.
// An inline guard in public/index.html applies the same value before first
// paint to avoid a flash of the wrong theme.

const STORAGE_KEY = 'aai-theme';

const ThemeContext = createContext({
  theme: 'dark',
  toggleTheme: () => {},
  setTheme: () => {},
});

function getInitialTheme() {
  if (typeof window === 'undefined') return 'dark';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch (_e) {
    // localStorage may be unavailable (private mode, etc.)
  }
  // The Figma "Final Design" is dark, so default to dark when the user has no
  // explicit saved preference (do not follow the OS `prefers-color-scheme`).
  return 'dark';
}

function applyThemeClass(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.classList.toggle('dark', theme === 'dark');
}

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(getInitialTheme);

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    const value = next === 'dark' ? 'dark' : 'light';
    setThemeState(value);
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch (_e) {
      // ignore — best effort
    }
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const value = prev === 'dark' ? 'light' : 'dark';
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch (_e) {
        // ignore — best effort
      }
      return value;
    });
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

export default ThemeContext;
